<template>
  <div class="email-bounce-page animated fadeIn">
    <div class="email-toolbar">
      <div>
        <h1>Email Delivery</h1>
      </div>
      <div class="email-toolbar-actions">
        <label class="email-span-control">
          <span>Period</span>
          <select v-model.number="selectedSpanHours" class="form-control form-control-sm">
            <option v-for="option in spanOptions" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>
        </label>
      </div>
    </div>

    <b-card class="panel-card status-summary" :class="{'status-summary-alert': overview.alarm_active}">
      <div class="summary-main">
        <div class="summary-state">
          <b-badge :variant="statusVariant" class="summary-status-badge">{{ statusLabel }}</b-badge>
          <strong>{{ activeAlerts.length }} active alerts</strong>
          <span v-if="overview.alarm_active">{{ nextAlertLabel }}</span>
        </div>
        <div class="summary-metrics">
          <div class="summary-metric">
            <span>Sent</span>
            <strong>{{ overview.sent_recent }}</strong>
            <small>accepted by SMTP</small>
          </div>
          <div class="summary-metric">
            <span>Delivery issues</span>
            <strong>{{ overview.alarmable_recent }}</strong>
            <small>needs review</small>
          </div>
          <div class="summary-metric">
            <span>Blacklist</span>
            <strong>{{ overview.blacklist_recent }}</strong>
            <small>alert at {{ overview.threshold }}</small>
          </div>
          <div class="summary-metric">
            <span>Deferred</span>
            <strong>{{ overview.deferred }}</strong>
            <small>alert at {{ overview.deferred_threshold }}</small>
          </div>
          <div class="summary-metric">
            <span>Informational</span>
            <strong>{{ overview.non_alarm_recent }}</strong>
            <small>not alerting</small>
          </div>
        </div>
      </div>
      <div v-if="overview.hard_bounces || overview.rate_limited || overview.last_alarm" class="summary-meta">
        <span v-if="overview.hard_bounces">{{ overview.hard_bounces }} hard bounces</span>
        <span v-if="overview.rate_limited">{{ overview.rate_limited }} rate limited</span>
        <span v-if="overview.last_alarm">last Discord {{ formatDate(overview.last_alarm) }}</span>
      </div>
    </b-card>

    <b-card v-if="activeAlerts.length" class="panel-card active-alert-card">
      <div slot="header" class="panel-header">
        <span><i class="fa fa-exclamation-triangle"></i> Active provider alerts</span>
      </div>
      <div class="alert-list">
        <div v-for="alert in activeAlerts" :key="alert.key" class="alert-row" :class="severityClass(alert.severity)">
          <div class="alert-main">
            <b-badge :variant="severityVariant(alert.severity)">{{ reasonLabel(alert.reason) }}</b-badge>
            <strong>{{ providerDisplay(alert) }}</strong>
            <span>{{ alert.message }}</span>
            <b-button
              v-if="canOpenRouting(alert)"
              v-b-tooltip.hover
              size="sm"
              variant="outline-info"
              class="route-shortcut-button"
              :title="routeShortcutLabel(alert)"
              @click="openRouteShortcut(alert)"
            >
              <i class="fa fa-random"></i>
            </b-button>
          </div>
          <div class="alert-stats">
            <span>{{ alert.count }} / {{ alert.threshold }}</span>
            <span>{{ alert.blacklist_recent }} blacklist</span>
            <span>{{ alert.deferred }} deferred</span>
            <span>{{ nextAlertText(alert) }}</span>
          </div>
        </div>
      </div>
    </b-card>

    <b-row>
      <b-col xl="8">
        <b-card class="panel-card">
          <div slot="header" class="panel-header">
            <span><i class="fa fa-server"></i> Provider status</span>
          </div>
          <div class="table-responsive provider-table-wrap">
            <table class="table table-sm table-hover email-bounce-table provider-table mb-0">
              <thead>
                <tr>
                  <th v-for="column in providerColumns" :key="column.key">
                    <button type="button" class="sort-header" @click="sortProvider(column.key)">
                      <span>{{ column.label }}</span>
                      <i :class="sortIcon(providerSort, column.key)"></i>
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="provider in sortedProviderHealth" :key="providerKey(provider)">
                  <td>
                    <b-badge :variant="severityVariant(provider.severity)">
                      {{ provider.severity || 'ok' }}
                    </b-badge>
                  </td>
                  <td>{{ sourceLabel(provider.source) }}</td>
                  <td>{{ provider.provider }}</td>
                  <td>
                    <div class="provider-domain-cell">
                      <span>{{ provider.domain || '-' }}</span>
                      <b-button
                        v-if="canOpenRouting(provider)"
                        v-b-tooltip.hover
                        size="sm"
                        variant="outline-info"
                        class="route-shortcut-button"
                        :title="routeShortcutLabel(provider)"
                        @click="openRouteShortcut(provider)"
                      >
                        <i class="fa fa-random"></i>
                      </b-button>
                    </div>
                  </td>
                  <td>{{ provider.sent_recent || 0 }}</td>
                  <td>{{ providerServerIssues(provider) }}</td>
                  <td :class="{'cell-warning': provider.blacklist_recent > 0}">{{ provider.blacklist_recent }}</td>
                  <td :class="{'cell-warning': provider.deferred > 0}">{{ provider.deferred }}</td>
                  <td>{{ reasonLabel(provider.top_reason) }}</td>
                </tr>
                <tr v-if="providerHealth.length === 0">
                  <td colspan="9" class="email-empty">No provider activity in this period.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </b-card>
      </b-col>

      <b-col xl="4">
        <b-card class="panel-card">
          <div slot="header" class="panel-header">
            <span><i class="fa fa-list"></i> Reasons</span>
          </div>
          <div class="reason-table-wrap">
            <table class="table table-sm email-bounce-table reason-table mb-0">
              <tbody>
                <tr v-for="reason in reasons" :key="reason.name">
                  <td>
                    <b-badge variant="secondary">{{ reasonLabel(reason.name) }}</b-badge>
                  </td>
                  <td class="text-right">{{ reason.count }}</td>
                </tr>
                <tr v-if="reasons.length === 0">
                  <td colspan="2" class="email-empty">No delivery issues in this period.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <b-card class="panel-card">
      <div slot="header" class="panel-header">
        <span><i class="fa fa-envelope-open"></i> Latest delivery events</span>
        <span class="email-muted">{{ filteredRecentEvents.length }} results - {{ selectedSpanLabel }}</span>
      </div>
      <div class="recent-search">
        <div class="input-group input-group-sm">
          <div class="input-group-prepend">
            <span class="input-group-text"><i class="fa fa-search"></i></span>
          </div>
          <input
            v-model.trim="recentSearch"
            class="form-control recent-search-input"
            type="search"
            placeholder="Search username, email, source, tracking ID, status or reason"
          >
          <div v-if="recentSearch" class="input-group-append">
            <b-button size="sm" variant="outline-light" @click="clearRecentSearch">
              <i class="fa fa-times"></i>
            </b-button>
          </div>
        </div>
      </div>
      <div class="table-responsive">
        <table class="table table-sm table-hover email-bounce-table bounce-table mb-0">
          <thead>
            <tr>
              <th v-for="column in recentColumns" :key="column.key">
                <button type="button" class="sort-header" @click="sortRecent(column.key)">
                  <span>{{ column.label }}</span>
                  <i :class="sortIcon(recentSort, column.key)"></i>
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="bounce in pagedRecentEvents" :key="bounceKey(bounce)">
              <td class="time-cell">
                <span>{{ formatDate(bounce.time) }}</span>
                <small v-if="hasSeparateSentTime(bounce)" class="email-muted">Sent {{ formatDate(bounce.sent_at) }}</small>
              </td>
              <td>{{ sourceLabel(bounce.source) }}</td>
              <td>{{ bounce.recipient || '-' }}</td>
              <td>
                <b-badge :variant="actionVariant(bounce)">{{ bounce.action || issueType(bounce) }}</b-badge>
              </td>
              <td>{{ bounce.status || '-' }}</td>
              <td>{{ bounce.username || '-' }}</td>
              <td class="diagnostic-cell">
                <span v-b-tooltip.hover :title="diagnosticText(bounce)">
                  {{ shortReason(bounce) }}
                </span>
              </td>
              <td class="tracking-cell">{{ bounce.tracking_id || bounce.message_id || '-' }}</td>
            </tr>
            <tr v-if="pagedRecentEvents.length === 0">
              <td colspan="8" class="email-empty">{{ recentEmptyText }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-if="filteredRecentEvents.length > 0" class="recent-pagination">
        <span class="email-muted">{{ recentRangeText }}</span>
        <div class="recent-page-controls">
          <select v-model.number="recentPerPage" class="form-control form-control-sm recent-page-size">
            <option v-for="size in recentPerPageOptions" :key="size" :value="size">{{ size }} / page</option>
          </select>
          <b-pagination
            v-model="recentPage"
            :total-rows="filteredRecentEvents.length"
            :per-page="recentPerPage"
            size="sm"
            class="mb-0"
          />
        </div>
      </div>
    </b-card>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import Permissions from '../../_permissions'

const EMPTY_OVERVIEW = {
  total_recent: 0,
  sent_recent: 0,
  hard_bounces: 0,
  deferred: 0,
  rate_limited: 0,
  alarmable_recent: 0,
  non_alarm_recent: 0,
  blacklist_recent: 0,
  threshold: 5,
  deferred_threshold: 3,
  window_minutes: 60,
  stats_window_hours: 24,
  recent_window_hours: 720,
  retention_hours: 720,
  log_read_limit: 20000,
  recent_limit: 2000,
  recent_events: 0,
  alarm_active: false,
  active_alerts: [],
  by_reason: {},
  by_provider: [],
  recent: []
}

export default {
  name: 'EmailBounces',
  data () {
    return {
      refreshTimer: null,
      selectedSpanHours: 24,
      spanOptions: [
        { value: 1, label: 'Up to 1 hour' },
        { value: 6, label: 'Up to 6 hours' },
        { value: 24, label: 'Up to 24 hours' },
        { value: 168, label: 'Up to 7 days' },
        { value: 720, label: 'Up to 30 days' }
      ],
      recentSearch: '',
      recentPage: 1,
      recentPerPage: 100,
      recentPerPageOptions: [50, 100, 250, 500],
      providerSort: { key: 'issues', direction: 'desc' },
      recentSort: { key: 'time', direction: 'desc' },
      providerColumns: [
        { key: 'severity', label: 'Status' },
        { key: 'source', label: 'Source' },
        { key: 'provider', label: 'Provider' },
        { key: 'domain', label: 'Domain' },
        { key: 'sent', label: 'Sent' },
        { key: 'issues', label: 'Delivery issues' },
        { key: 'blacklist', label: 'Blacklist' },
        { key: 'deferred', label: 'Deferred' },
        { key: 'reason', label: 'Reason' }
      ],
      recentColumns: [
        { key: 'time', label: 'Time' },
        { key: 'source', label: 'Source' },
        { key: 'recipient', label: 'Recipient' },
        { key: 'action', label: 'Action' },
        { key: 'status', label: 'Status' },
        { key: 'username', label: 'User' },
        { key: 'reason', label: 'Reason' },
        { key: 'tracking', label: 'Tracking' }
      ]
    }
  },
  computed: {
    ...mapGetters({
      authUser: 'authUser',
      storeOverview: 'EmailBounceOverview'
    }),
    overview () {
      return Object.assign({}, EMPTY_OVERVIEW, this.storeOverview || {})
    },
    activeAlerts () {
      return this.overview.active_alerts || []
    },
    recentEvents () {
      return this.overview.recent || []
    },
    normalizedRecentSearch () {
      return this.recentSearch.toLowerCase().trim()
    },
    filteredRecentEvents () {
      let rows = this.recentEvents
      if (this.normalizedRecentSearch) {
        const terms = this.normalizedRecentSearch.split(/\s+/).filter(Boolean)
        rows = rows.filter(bounce => {
          const haystack = this.recentSearchText(bounce)
          return terms.every(term => haystack.indexOf(term) !== -1)
        })
      }
      return this.sortedRows(rows, this.recentSort, this.recentSortValue)
    },
    pagedRecentEvents () {
      const start = (this.recentPage - 1) * this.recentPerPage
      return this.filteredRecentEvents.slice(start, start + this.recentPerPage)
    },
    sortedProviderHealth () {
      return this.sortedRows(this.providerHealth, this.providerSort, this.providerSortValue)
    },
    recentRangeText () {
      if (this.filteredRecentEvents.length === 0) return 'No results'
      const start = (this.recentPage - 1) * this.recentPerPage + 1
      const end = Math.min(start + this.recentPerPage - 1, this.filteredRecentEvents.length)
      return `Showing ${start}-${end} of ${this.filteredRecentEvents.length}`
    },
    recentEmptyText () {
      if (this.recentEvents.length === 0) return 'No delivery events found.'
      return 'No matching delivery events.'
    },
    reasons () {
      const reasons = this.overview.by_reason || {}
      return Object.keys(reasons)
        .map(name => ({ name, count: reasons[name] }))
        .sort((a, b) => b.count - a.count)
    },
    providers () {
      return this.overview.by_provider || []
    },
    providerHealth () {
      return this.providers.filter(provider => this.providerServerIssues(provider) > 0 || (provider.sent_recent || 0) > 0)
    },
    canManageRouting () {
      return this.hasPermission('EmailRoutingView') && this.hasPermission('EmailRoutingEdit')
    },
    statusVariant () {
      if (this.activeAlerts.some(alert => alert.severity === 'critical')) return 'danger'
      if (this.overview.alarm_active) return 'warning'
      if (this.reviewIssueCount > 0) return 'info'
      return 'success'
    },
    statusLabel () {
      if (this.activeAlerts.some(alert => alert.severity === 'critical')) return 'Critical'
      if (this.overview.alarm_active) return 'Warning'
      if (this.reviewIssueCount > 0) return 'Review'
      return 'Normal'
    },
    reviewIssueCount () {
      return this.overview.alarmable_recent || 0
    },
    nextAlertLabel () {
      if (!this.overview.alarm_active) return 'No active alerts'
      if (!this.overview.next_alert_at) return 'Ready to notify'
      if (new Date(this.overview.next_alert_at).getTime() <= Date.now()) return 'Ready to notify'
      return `Next Discord after ${this.formatDate(this.overview.next_alert_at)}`
    },
    selectedSpanLabel () {
      const option = this.spanOptions.find(option => option.value === this.selectedSpanHours)
      return option ? option.label : `${this.selectedSpanHours} hours`
    }
  },
  watch: {
    selectedSpanHours () {
      this.recentPage = 1
      this.refresh()
    },
    normalizedRecentSearch () {
      this.recentPage = 1
    },
    recentPerPage () {
      this.recentPage = 1
    },
    filteredRecentEvents () {
      const maxPage = Math.max(1, Math.ceil(this.filteredRecentEvents.length / this.recentPerPage))
      if (this.recentPage > maxPage) this.recentPage = maxPage
    }
  },
  mounted () {
    this.refresh()
    this.refreshTimer = window.setInterval(() => this.refresh(), 30000)
  },
  beforeDestroy () {
    if (this.refreshTimer) {
      window.clearInterval(this.refreshTimer)
    }
  },
  methods: {
    refresh () {
      this.emit('getEmailBounceOverview', { span_hours: this.selectedSpanHours })
    },
    clearRecentSearch () {
      this.recentSearch = ''
    },
    sortProvider (key) {
      this.providerSort = this.nextSort(this.providerSort, key)
    },
    sortRecent (key) {
      this.recentSort = this.nextSort(this.recentSort, key)
      this.recentPage = 1
    },
    nextSort (current, key) {
      if (current.key === key) {
        return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
      }
      return { key, direction: key === 'time' || key === 'sent' || key === 'issues' || key === 'blacklist' || key === 'deferred' ? 'desc' : 'asc' }
    },
    sortIcon (sort, key) {
      if (sort.key !== key) return 'fa fa-sort sort-icon sort-icon-muted'
      return sort.direction === 'asc' ? 'fa fa-sort-asc sort-icon' : 'fa fa-sort-desc sort-icon'
    },
    sortedRows (rows, sort, valueFn) {
      const sorted = (rows || []).slice()
      sorted.sort((a, b) => this.compareSortValues(valueFn(a, sort.key), valueFn(b, sort.key), sort.direction))
      return sorted
    },
    compareSortValues (a, b, direction) {
      const multiplier = direction === 'asc' ? 1 : -1
      const emptyA = a === null || a === undefined || a === ''
      const emptyB = b === null || b === undefined || b === ''
      if (emptyA && emptyB) return 0
      if (emptyA) return 1
      if (emptyB) return -1
      if (typeof a === 'number' && typeof b === 'number') return (a - b) * multiplier
      return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' }) * multiplier
    },
    providerSortValue (provider, key) {
      const severityRank = { critical: 4, warning: 3, watch: 2, info: 1, ok: 0 }
      switch (key) {
        case 'severity': return severityRank[provider.severity || 'ok'] || 0
        case 'source': return this.sourceLabel(provider.source)
        case 'provider': return provider.provider || ''
        case 'domain': return provider.domain || ''
        case 'sent': return provider.sent_recent || 0
        case 'issues': return this.providerServerIssues(provider)
        case 'blacklist': return provider.blacklist_recent || 0
        case 'deferred': return provider.deferred || 0
        case 'reason': return this.reasonLabel(provider.top_reason)
        default: return ''
      }
    },
    recentSortValue (bounce, key) {
      switch (key) {
        case 'time': return this.timestampValue(bounce.time)
        case 'source': return this.sourceLabel(bounce.source)
        case 'recipient': return bounce.recipient || ''
        case 'action': return bounce.action || this.issueType(bounce)
        case 'status': return bounce.status || ''
        case 'username': return bounce.username || ''
        case 'reason': return this.shortReason(bounce)
        case 'tracking': return bounce.tracking_id || bounce.message_id || ''
        default: return ''
      }
    },
    timestampValue (value) {
      if (!value) return null
      const timestamp = new Date(value).getTime()
      return Number.isNaN(timestamp) ? null : timestamp
    },
    formatDate (value) {
      if (!value) return '-'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return '-'
      return date.toLocaleString()
    },
    providerDisplay (item) {
      if (!item) return 'unknown'
      const provider = item.domain && item.domain !== item.provider
        ? `${item.provider} (${item.domain})`
        : (item.provider || item.domain || 'unknown')
      if (item.source && item.source !== 'unknown') return `${provider} via ${this.sourceLabel(item.source)}`
      return provider
    },
    canOpenRouting (item) {
      return Boolean(this.canManageRouting && item && this.routeShortcutDomain(item) && this.isRouteShortcutRelevant(item))
    },
    isRouteShortcutRelevant (item) {
      const reason = String((item && (item.reason || item.top_reason)) || '').toLowerCase()
      return reason === 'blacklist' || reason === 'deferred' || (item.blacklist_recent || 0) > 0 || (item.deferred || 0) > 0
    },
    routeShortcutDomain (item) {
      return String((item && item.domain) || '').trim().toLowerCase()
    },
    suggestedRouteForSource (source) {
      const value = String(source || '').trim().toLowerCase()
      if (value === 'vps') return 'namecheap'
      if (value === 'namecheap') return 'vps'
      return 'vps'
    },
    routeShortcutLabel (item) {
      return `Add route to ${this.sourceLabel(this.suggestedRouteForSource(item && item.source))}`
    },
    openRouteShortcut (item) {
      const domain = this.routeShortcutDomain(item)
      if (!domain) return
      const route = this.suggestedRouteForSource(item && item.source)
      const reason = this.reasonLabel((item && (item.reason || item.top_reason)) || '')
      this.$router.push({
        path: '/admin/email-routing',
        query: {
          match: 'domain',
          pattern: domain,
          route,
          note: reason && reason !== '-' ? `Reroute after ${reason}` : 'Reroute from delivery monitor'
        }
      })
    },
    sourceLabel (source) {
      const value = String(source || '').trim()
      if (!value || value === 'unknown') return '-'
      const labels = {
        vps: 'VPS',
        namecheap: 'Namecheap'
      }
      return labels[value.toLowerCase()] || value
    },
    providerKey (provider) {
      return `${provider.source || ''}-${provider.provider || ''}-${provider.domain || ''}`
    },
    providerServerIssues (provider) {
      return (provider.blacklist_recent || 0) + (provider.deferred || 0) + (provider.hard_bounces || 0)
    },
    reasonLabel (reason) {
      const labels = {
        blacklist: 'Blacklist',
        deferred: 'Deferred',
        delivery_issues: 'Delivery issues',
        invalid_recipient: 'User not found',
        mailbox_full: 'Mailbox full',
        timeout: 'Timeout',
        rate_limited: 'Rate limited',
        sent: 'Sent',
        unknown: 'Unknown'
      }
      return labels[reason] || reason || '-'
    },
    severityVariant (severity) {
      if (severity === 'critical') return 'danger'
      if (severity === 'warning') return 'warning'
      if (severity === 'watch') return 'info'
      if (severity === 'info') return 'primary'
      return 'success'
    },
    severityClass (severity) {
      return `alert-${severity || 'ok'}`
    },
    nextAlertText (alert) {
      if (!alert.next_alert_at) return 'Ready to notify'
      if (new Date(alert.next_alert_at).getTime() <= Date.now()) return 'Ready to notify'
      return `Discord after ${this.formatDate(alert.next_alert_at)}`
    },
    diagnosticText (bounce) {
      return this.redactIPs(bounce.diagnostic || bounce.raw || '')
    },
    shortReason (bounce) {
      if (this.isSentEvent(bounce)) return bounce.subject || 'Accepted by SMTP'
      const text = this.diagnosticText(bounce) || bounce.action || ''
      if (!text) return '-'
      return text.length > 120 ? `${text.substring(0, 120)}...` : text
    },
    redactIPs (value) {
      return String(value || '')
        .replace(/\b(?:\d{1,3}\.){3}\d{1,3}\b/g, '[ip]')
        .replace(/\b(?:[0-9a-f]{1,4}:){2,}[0-9a-f]{1,4}\b/gi, '[ip]')
    },
    recentSearchText (bounce) {
      return [
        bounce.time,
        bounce.sent_at,
        bounce.event,
        bounce.source,
        bounce.tracking_id,
        bounce.message_id,
        bounce.purpose,
        bounce.username,
        bounce.recipient,
        bounce.sender,
        bounce.subject,
        bounce.action,
        bounce.status,
        bounce.reason,
        this.reasonLabel(bounce.reason),
        this.impactLabel(bounce),
        this.redactIPs(bounce.diagnostic),
        bounce.remote_mta,
        this.redactIPs(bounce.raw),
        this.issueType(bounce)
      ].filter(Boolean).join(' ').toLowerCase()
    },
    issueType (bounce) {
      if (this.isSentEvent(bounce)) return 'sent'
      if (bounce.status && bounce.status.indexOf('4.') === 0) return 'deferred'
      if (bounce.status && bounce.status.indexOf('5.') === 0) return 'failed'
      if (bounce.status === 'rate_limited' || bounce.event === 'rate_limited') return 'rate_limited'
      return '-'
    },
    actionVariant (bounce) {
      const action = bounce.action || this.issueType(bounce)
      if (action === 'sent') return 'success'
      if (action === 'failed') return 'danger'
      if (action === 'delayed' || action === 'deferred') return 'warning'
      if (action === 'rate_limited') return 'info'
      return 'secondary'
    },
    impactLabel (bounce) {
      if (this.isSentEvent(bounce)) return 'Delivered'
      if (bounce.alarmable === false) return 'Failed'
      return 'Delivery issue'
    },
    impactVariant (bounce) {
      if (this.isSentEvent(bounce)) return 'success'
      if (bounce.alarmable === false) return 'danger'
      return 'warning'
    },
    isSentEvent (bounce) {
      return bounce.action === 'sent' || bounce.event === 'sent' || bounce.status === 'sent'
    },
    hasSeparateSentTime (bounce) {
      if (!bounce.sent_at || !bounce.time) return false
      return new Date(bounce.sent_at).getTime() !== new Date(bounce.time).getTime()
    },
    bounceKey (bounce) {
      return `${bounce.time || ''}-${bounce.source || ''}-${bounce.recipient || ''}-${bounce.message_id || ''}-${bounce.tracking_id || ''}`
    },
    hasPermission (name) {
      const permission = this.findPermission(name)
      const user = this.authUser
      if (!user || !permission) return false
      if (permission.and(user.deny).neq(0)) return false
      if (permission.and(user.allow).neq(0)) return true
      return permission.and(user.permission).neq(0)
    },
    findPermission (name) {
      for (let category in Permissions) {
        for (let permission in Permissions[category].Permissions) {
          if (permission === name) {
            return Permissions[category].Permissions[permission].Permission
          }
        }
      }
      return null
    }
  }
}
</script>

<style scoped>
.email-bounce-page {
  color: #e4e7ea;
}

.email-toolbar {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
}

.email-toolbar h1 {
  font-size: 1.35rem;
  font-weight: 600;
  letter-spacing: 0;
  margin: 0;
}

.email-toolbar-actions {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}

.email-span-control {
  align-items: center;
  display: flex;
  gap: 8px;
  margin: 0;
}

.email-span-control span {
  color: #a4abb3;
  font-size: 0.76rem;
  text-transform: uppercase;
}

.email-span-control .form-control {
  background: #2f353a;
  border-color: #3a4149;
  color: #e4e7ea;
  width: 132px;
}

.email-muted {
  color: #a4abb3;
  font-size: 0.82rem;
}

.status-badge {
  font-size: 0.78rem;
  min-width: 72px;
  padding: 7px 10px;
}

.summary-status-badge {
  color: #fff !important;
  font-size: 0.82rem;
  justify-self: start;
  min-width: 76px;
  padding: 7px 10px;
  text-align: center;
}

.panel-card {
  margin-bottom: 16px;
}

.panel-header {
  align-items: center;
  display: flex;
  justify-content: space-between;
}

.provider-table-wrap,
.reason-table-wrap {
  max-height: 360px;
  overflow-y: auto;
}

.provider-table thead th {
  position: sticky;
  top: 0;
  z-index: 2;
}

.status-summary {
  border-left: 3px solid #53616d;
}

.status-summary-alert {
  border-left-color: #f86c6b;
}

.summary-main {
  display: grid;
  gap: 12px;
  grid-template-columns: 1fr;
}

.summary-state {
  align-items: center;
  border-bottom: 1px solid #3a4149;
  display: flex;
  flex-wrap: wrap;
  gap: 8px 12px;
  padding-bottom: 10px;
}

.summary-state strong {
  color: #f0f3f5;
  font-size: 0.95rem;
  line-height: 1.2;
}

.summary-state span {
  color: #d6d9dc;
  font-size: 0.82rem;
  line-height: 1.25;
}

.summary-metrics {
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(5, minmax(126px, 168px));
  justify-content: flex-start;
}

.summary-metric {
  background: #2f353a;
  border: 1px solid #3a4149;
  min-height: 86px;
  padding: 10px;
}

.summary-metric span,
.summary-metric small {
  color: #a4abb3;
  display: block;
  font-size: 0.72rem;
  text-transform: uppercase;
}

.summary-metric strong {
  color: #f0f3f5;
  display: block;
  font-size: 1.55rem;
  line-height: 1.2;
  margin: 5px 0;
}

.summary-meta {
  border-top: 1px solid #3a4149;
  color: #a4abb3;
  display: flex;
  flex-wrap: wrap;
  font-size: 0.8rem;
  gap: 12px;
  margin-top: 8px;
  padding-top: 8px;
}

.active-alert-card {
  border-color: #66512c;
}

.alert-list {
  display: grid;
  gap: 10px;
}

.alert-row {
  border-left: 3px solid #53616d;
  padding: 10px 12px;
}

.alert-critical {
  border-left-color: #f86c6b;
}

.alert-warning {
  border-left-color: #ffc107;
}

.alert-main {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.alert-stats {
  color: #a4abb3;
  display: flex;
  flex-wrap: wrap;
  font-size: 0.8rem;
  gap: 12px;
  margin-top: 8px;
}

.recent-search {
  border-bottom: 1px solid #3a4149;
  padding: 10px 12px 12px;
}

.recent-pagination {
  align-items: center;
  border-top: 1px solid #3a4149;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: space-between;
  padding: 10px 12px;
}

.recent-page-controls {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: flex-end;
}

.recent-page-size {
  background: #2f353a;
  border-color: #3a4149;
  color: #e4e7ea;
  width: 124px;
}

.recent-search .input-group {
  max-width: 560px;
}

.recent-search .input-group-text,
.recent-search-input {
  background: #2f353a;
  border-color: #3a4149;
  color: #e4e7ea;
}

.recent-search-input::placeholder {
  color: #8f9aa3;
}

.recent-search-input:focus {
  background: #343b41;
  border-color: #63c2de;
  box-shadow: none;
  color: #fff;
}

.email-bounce-table {
  color: #e4e7ea;
}

.email-bounce-table th,
.email-bounce-table td {
  border-color: #3a4149;
  color: #e4e7ea;
  vertical-align: middle;
}

.email-bounce-table thead th {
  background: #2f353a;
  color: #f0f3f5;
  font-size: 0.74rem;
  letter-spacing: 0;
  text-transform: uppercase;
  white-space: nowrap;
}

.sort-header {
  align-items: center;
  background: transparent;
  border: 0;
  color: inherit;
  cursor: pointer;
  display: inline-flex;
  font: inherit;
  gap: 6px;
  letter-spacing: inherit;
  padding: 0;
  text-align: left;
  text-transform: inherit;
  white-space: nowrap;
}

.sort-header:focus {
  outline: 0;
}

.sort-icon {
  color: #c8ced3;
  font-size: 0.72rem;
  width: 10px;
}

.sort-icon-muted {
  color: #73818f;
}

.email-bounce-table tbody tr:hover td {
  background: #343b41;
  color: #fff;
}

.provider-table td,
.bounce-table td,
.bounce-table th {
  white-space: nowrap;
}

.provider-domain-cell {
  align-items: center;
  display: inline-flex;
  gap: 8px;
}

.route-shortcut-button {
  line-height: 1;
  min-width: 30px;
}

.diagnostic-cell {
  max-width: 420px;
  white-space: normal !important;
}

.tracking-cell {
  font-family: Consolas, Monaco, monospace;
  font-size: 0.76rem;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.time-cell {
  min-width: 156px;
}

.time-cell small {
  display: block;
  margin-top: 2px;
}

.cell-warning {
  color: #ffc107 !important;
  font-weight: 600;
}

.email-empty {
  color: #a4abb3 !important;
  padding: 18px 8px !important;
  text-align: center;
}

@media (max-width: 1199px) {
  .summary-metrics {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 767px) {
  .email-toolbar {
    align-items: flex-start;
    flex-direction: column;
    gap: 10px;
  }

  .email-toolbar-actions {
    justify-content: flex-start;
  }

  .summary-metrics {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
